// Copyright 2022, Imprivata, Inc.  All rights reserved.

/**
 * Base URL for pa-digital-credentials-svc.
 * Local dev: set VITE_DIGITAL_CREDENTIALS_API_URL in .env.local
 *   e.g. VITE_DIGITAL_CREDENTIALS_API_URL=https://localhost:7000/api/v1/
 * Deployed: derived from subdomain pattern or set via window._appConfig.
 */
function getDigitalCredentialsBaseUrl(): string {
  const env = import.meta.env;
  if (env.VITE_DIGITAL_CREDENTIALS_API_URL) {
    return env.VITE_DIGITAL_CREDENTIALS_API_URL;
  }
  if (window.location.host.includes('localhost')) {
    return 'https://localhost:7000/api/v1/';
  }
  return 'https://pa.sys.dev.imprivata.cloud/pa-digital-credentials/api/v1/';
}

export interface DocumentTypeRequest {
  docType: string;
  nameSpaces: Record<string, Array<{ name: string; intentToRetain: boolean }>>;
}

export interface CreateSessionRequest {
  origin: string;
  documentTypes: DocumentTypeRequest[];
}

export interface CreateSessionResponse {
  sessionId: string;
  deviceRequest: string;
  encryptionInfo: string;
  expiresAt: string;
}

/**
 * Get tenant ID from URL query parameters.
 * Tenant ID is passed via Impr-Tenant-Id query param.
 */
function getTenantId(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('Impr-Tenant-Id');
}

export async function createDigitalIdSession(
  request: CreateSessionRequest,
): Promise<CreateSessionResponse> {
  // Validate input
  if (request.documentTypes.length === 0) {
    return Promise.reject({
      status: 400,
      body: { error: 'documentTypes array cannot be empty' },
    });
  }

  const baseUrl = getDigitalCredentialsBaseUrl();
  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  // Add Impr-Tenant-Id header if tenant ID is in URL
  const tenantId = getTenantId();
  if (tenantId) {
    headers['Impr-Tenant-Id'] = tenantId;
  }

  const response = await fetch(`${baseUrl}iso/sessions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    return Promise.reject({ status: response.status, body: errorBody });
  }
  return response.json() as Promise<CreateSessionResponse>;
}
